<template>
  <div class="min-h-screen bg-[#f8fafc] text-slate-800 font-sans selection:bg-cyan-500 selection:text-white flex flex-col relative overflow-hidden">
    <!-- Fondo premium con formas sutiles -->
    <div class="absolute inset-0 z-0 pointer-events-none overflow-hidden">
      <div class="absolute top-0 right-1/4 w-96 h-96 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
      <div class="absolute top-0 left-1/4 w-96 h-96 bg-cyan-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
      <div class="absolute -bottom-32 right-1/3 w-96 h-96 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
    </div>

    <!-- Banner de Tasa de Cambio Premium -->
    <div v-if="tasaVenta > 0" class="w-full bg-gradient-to-r from-blue-700 via-indigo-700 to-indigo-800 text-white text-xs py-2.5 px-4 flex flex-wrap justify-center items-center gap-x-4 gap-y-1 font-medium shadow-inner z-50 relative border-b border-white/10">
      <div class="flex items-center gap-2">
        <span class="relative flex h-2 w-2">
          <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span class="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
        </span>
        <span class="tracking-wide">Tasa Oficial del Día: <strong>1 USD = {{ formatPrice(tasaVenta) }} Bs</strong></span>
      </div>
      <span v-if="fuenteTasa" class="text-blue-100 text-[10px] bg-white/15 px-2.5 py-0.5 rounded-full border border-white/10 uppercase tracking-wider font-semibold">Fuente: {{ fuenteTasa }}</span>
    </div>

    <!-- Navbar Premium con Glassmorphism -->
    <nav class="sticky top-0 w-full z-40 bg-white/80 backdrop-blur-xl shadow-md border-b border-slate-100 transition-all duration-300">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16 md:h-20">
          
          <!-- Logo & Brand Name -->
          <router-link to="/" class="flex items-center gap-3 transition-all duration-300 hover:scale-[1.02] cursor-pointer">
            <img src="@/assets/logo/jj.png" alt="Logo SYSTEM JJ" class="h-10 md:h-12 w-auto object-contain rounded-xl shadow-md border border-slate-100" />
            <div class="flex flex-col">
              <span class="text-base md:text-lg font-black tracking-tight bg-gradient-to-r from-slate-900 via-slate-800 to-blue-700 bg-clip-text text-transparent">
                SYSTEM JJ
              </span>
              <span class="text-[9px] text-slate-400 font-bold uppercase tracking-wider -mt-1">Servicios & Repuestos</span>
            </div>
          </router-link>
          
          <!-- Desktop Menu -->
          <div class="hidden md:flex items-center space-x-1 lg:space-x-2">
            <router-link to="/" class="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:text-blue-600 hover:bg-blue-50/50 transition-all duration-300">
              Inicio
            </router-link>
            <router-link to="/catalogo-servicios" class="px-4 py-2 rounded-xl text-sm font-semibold text-blue-600 bg-blue-50/50 transition-all duration-300">
              Catálogo de Servicios
            </router-link>
            <router-link to="/catalogo-productos" class="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:text-blue-600 hover:bg-blue-50/50 transition-all duration-300">
              Catálogo de Productos
            </router-link>
            
            <router-link to="/login" class="ml-4 px-6 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 hover:shadow-xl hover:scale-105 transition-all duration-300 shadow-md">
              Iniciar Sesión
            </router-link>
          </div>

          <!-- Mobile Menu Button -->
          <div class="md:hidden flex items-center">
            <button @click="mobileMenuOpen = !mobileMenuOpen" class="relative w-10 h-10 rounded-xl bg-slate-50 text-slate-800 hover:text-blue-600 hover:bg-blue-50 transition-all duration-300 flex items-center justify-center border border-slate-100 shadow-sm">
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Mobile Menu Dropdown -->
      <transition 
        enter-active-class="transition duration-300 ease-out" 
        enter-from-class="opacity-0 -translate-y-4 scale-95" 
        enter-to-class="opacity-100 translate-y-0 scale-100" 
        leave-active-class="transition duration-200 ease-in" 
        leave-from-class="opacity-100 translate-y-0 scale-100" 
        leave-to-class="opacity-0 -translate-y-4 scale-95">
        <div v-if="mobileMenuOpen" class="md:hidden bg-white/95 backdrop-blur-xl border-b border-slate-100 shadow-2xl absolute w-full z-40">
          <div class="px-6 pt-4 pb-6 space-y-2">
            <router-link to="/" @click="mobileMenuOpen = false" class="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-semibold text-slate-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-300">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/></svg>
              Inicio
            </router-link>
            <router-link to="/catalogo-servicios" @click="mobileMenuOpen = false" class="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-semibold text-blue-600 bg-blue-50 transition-all duration-300">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>
              Catálogo de Servicios
            </router-link>
            <router-link to="/catalogo-productos" @click="mobileMenuOpen = false" class="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-semibold text-slate-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-300">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/></svg>
              Catálogo de Productos
            </router-link>
            <router-link to="/login" @click="mobileMenuOpen = false" class="flex items-center justify-center gap-2 mt-4 px-4 py-3 rounded-xl text-base font-bold bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-md">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"/></svg>
              Iniciar Sesión
            </router-link>
          </div>
        </div>
      </transition>
    </nav>

    <!-- Main Content -->
    <div class="flex-grow relative z-10 py-16 md:py-24">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center">
        
        <!-- Header Céntrico y Elegante -->
        <div class="text-center max-w-3xl mx-auto mb-16">
          <span class="text-blue-600 font-bold tracking-widest uppercase text-sm mb-4 block">Soporte Técnico</span>
          <h1 class="text-5xl md:text-6xl font-extrabold text-slate-900 mb-6 tracking-tight">Catálogo de Servicios</h1>
          <div class="w-24 h-1 bg-blue-500 mx-auto rounded-full mb-6"></div>
          <p class="text-slate-500 text-lg md:text-xl font-light leading-relaxed">Soluciones técnicas rápidas, profesionales y garantizadas para todos tus equipos informáticos.</p>
        </div>
        
        <!-- Buscador Céntrico -->
        <div class="w-full max-w-2xl mx-auto mb-20 relative group">
          <div class="absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none">
            <svg class="h-6 w-6 text-slate-400 group-focus-within:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
          </div>
          <input v-model="searchQuery" type="text" placeholder="Buscar servicio por nombre o descripción..." class="block w-full pl-16 pr-6 py-5 border-0 ring-1 ring-slate-200 rounded-full text-lg bg-white/80 backdrop-blur-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]" />
        </div>
        
        <!-- Loading State -->
        <div v-if="loadingServicios" class="flex flex-col items-center justify-center my-24 w-full">
          <div class="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500 mb-6"></div>
          <p class="text-slate-500 font-medium tracking-wide">Cargando servicios...</p>
        </div>
        
        <!-- Empty State -->
        <div v-else-if="filteredServicios.length === 0" class="text-center py-24 w-full max-w-2xl mx-auto bg-white/50 backdrop-blur-md rounded-[3rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white">
          <div class="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg class="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
          </div>
          <h3 class="text-2xl font-bold text-slate-900 mb-3">No hay servicios a la vista</h3>
          <p class="text-slate-500 text-lg max-w-md mx-auto">No hemos encontrado lo que buscas. Intenta con otras palabras.</p>
          <button v-if="searchQuery" @click="searchQuery = ''" class="mt-8 px-8 py-3 bg-slate-900 hover:bg-blue-600 text-white rounded-full font-bold transition-colors shadow-lg">Mostrar todos</button>
        </div>

        <!-- Services Grid -->
        <div v-else class="w-full">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 w-full">
            <div v-for="servicio in paginatedServicios" :key="servicio.idServicio" class="bg-white/80 backdrop-blur-lg rounded-[2rem] p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-500 border border-white flex flex-col items-center text-center h-full group transform hover:-translate-y-2 relative overflow-hidden">
              
              <!-- Decorative element -->
              <div class="absolute -top-16 -right-16 w-40 h-40 bg-gradient-to-bl from-blue-50/80 to-transparent rounded-full opacity-50 z-0 pointer-events-none group-hover:scale-150 transition-transform duration-700"></div>
              
              <div class="relative z-10 flex flex-col h-full w-full items-center">
                <div class="w-20 h-20 rounded-[1.5rem] bg-blue-50/80 text-blue-600 flex items-center justify-center mb-6 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-500 shadow-sm border border-white">
                  <svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                </div>
                
                <h3 class="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors leading-tight h-14 flex items-center justify-center">{{ servicio.nombre }}</h3>
                
                <!-- Descripción corta y de altura estricta -->
                <p class="text-slate-500 mb-6 w-full text-[13px] font-light leading-relaxed px-2 h-16 overflow-hidden line-clamp-3">{{ servicio.descripcion || 'Servicio técnico especializado y garantizado por nuestros expertos.' }}</p>
                
                <div class="mt-auto pt-6 border-t border-slate-100 w-full flex flex-col items-center space-y-4">
                  <div class="flex flex-col items-center space-y-1">
                    <span class="block text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-1">Precio Estimado</span>
                    <div class="flex flex-col items-center">
                      <p class="text-3xl font-black text-slate-900 tracking-tight">${{ formatPrice(servicio.precioUsd) }}</p>
                      <p v-if="tasaVenta > 0" class="text-xs font-bold text-slate-500 mt-0.5">
                        ≈ {{ formatBsPrice(servicio.precioUsd) }}
                      </p>
                    </div>
                  </div>
                  
                  <!-- Plan de pago Cashea (17% recargo) -->
                  <div class="mt-3 p-3 bg-purple-50/80 border border-purple-100 rounded-2xl flex flex-col items-center justify-center text-center shadow-sm w-full">
                    <div class="flex items-center gap-1.5 mb-1 bg-purple-100 px-2 py-0.5 rounded-full">
                      <span class="w-1.5 h-1.5 rounded-full bg-purple-600 animate-pulse"></span>
                      <span class="text-[9px] text-purple-800 font-extrabold uppercase tracking-wide">Precio Cashea</span>
                    </div>
                    <div class="text-sm font-extrabold text-purple-700">
                      ${{ formatPrice(servicio.precioUsd * 1.17) }}
                    </div>
                    <div class="text-[9px] text-purple-500 font-semibold mt-0.5">
                      (Incluye 17% de recargo)
                    </div>
                  </div>
                  
                  <!-- Botones de Acción -->
                  <div class="flex flex-col gap-2 w-full pt-2">
                    <a :href="getWhatsAppContratarUrl(servicio)" target="_blank" class="w-full flex items-center justify-center gap-2 py-3 bg-slate-900 text-white rounded-xl text-sm font-bold hover:bg-blue-600 transition-colors shadow-md">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                      Contratar Servicio
                    </a>
                    <a :href="getWhatsAppConsultarUrl(servicio)" target="_blank" class="w-full flex items-center justify-center gap-2 py-3 bg-blue-50 text-blue-600 border border-blue-200 rounded-xl text-sm font-bold hover:bg-blue-100 hover:text-blue-700 transition-colors">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.711.927 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.274.072.376-.043c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564.289.13.332.202c.045.072.045.419-.099.824z"/></svg>
                      Consultar WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Premium Pagination (Centrado) -->
          <div v-if="totalPages > 1" class="mt-24 flex flex-col items-center w-full">
            <div class="flex items-center space-x-2 bg-white/80 backdrop-blur-xl p-2.5 rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-white">
              <button @click="currentPage--" :disabled="currentPage === 1" class="w-12 h-12 rounded-full flex items-center justify-center transition-all" :class="currentPage === 1 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100 hover:text-blue-600'">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path></svg>
              </button>
              
              <div class="hidden sm:flex space-x-1 px-2">
                <button v-for="page in visiblePages" :key="page" @click="currentPage = page" class="w-12 h-12 rounded-full font-bold text-sm transition-all" :class="currentPage === page ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20' : 'text-slate-600 hover:bg-slate-100 hover:text-blue-600'">
                  {{ page }}
                </button>
              </div>
              <div class="sm:hidden text-sm font-bold text-slate-700 px-6">
                Página {{ currentPage }} de {{ totalPages }}
              </div>
              
              <button @click="currentPage++" :disabled="currentPage === totalPages" class="w-12 h-12 rounded-full flex items-center justify-center transition-all" :class="currentPage === totalPages ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100 hover:text-blue-600'">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
              </button>
            </div>
          </div>
          
        </div>
      </div>
    </div>
    
    <!-- Footer Elegante -->
    <footer class="bg-white border-t border-slate-100 py-12 mt-20 relative z-10">
      <div class="absolute -top-16 left-2 md:left-6 z-[100]">
        <img src="@/assets/logo/jj.png" alt="Logo SYSTEM JJ" class="h-28 md:h-32 w-auto object-contain mix-blend-multiply" />
      </div>
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-end relative pt-8">
          <div class="flex flex-wrap justify-end gap-8 mt-4">
             <router-link to="/" class="text-sm font-bold text-slate-500 hover:text-blue-600 tracking-wider uppercase transition-colors">Inicio</router-link>
             <router-link to="/catalogo-productos" class="text-sm font-bold text-slate-500 hover:text-blue-600 tracking-wider uppercase transition-colors">Productos</router-link>
             <router-link to="/login" class="text-sm font-bold text-slate-500 hover:text-blue-600 tracking-wider uppercase transition-colors">Sistema</router-link>
          </div>
          <div class="w-12 h-1 bg-slate-200 rounded-full"></div>
          <p class="text-slate-400 text-sm font-light">&copy; {{ new Date().getFullYear() }} SYSTEM JJ. Todos los derechos reservados.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { obtenerServicios } from '@/api/servicios';
import { obtenerTasaVentaActual } from '@/api/tasaVenta';

export default {
  name: 'CatalogoServicios',
  setup() {
    const servicios = ref([]);
    const loadingServicios = ref(true);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const itemsPerPage = 9; // 9 servicios por página
    const mobileMenuOpen = ref(false);
    const tasaVenta = ref(0);
    const fuenteTasa = ref('');

    const fetchServicios = async () => {
      try {
        loadingServicios.value = true;
        const result = await obtenerServicios('activos');
        if (result && result.success) {
          servicios.value = result.data;
        }
      } catch (error) {
        console.error('Error cargando servicios:', error);
      } finally {
        loadingServicios.value = false;
      }
    };

    const filteredServicios = computed(() => {
      let filtered = servicios.value;
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter(s => 
          (s.nombre && s.nombre.toLowerCase().includes(query)) ||
          (s.descripcion && s.descripcion.toLowerCase().includes(query)) ||
          (s.codigo && s.codigo.toLowerCase().includes(query))
        );
      }
      return filtered;
    });

    const totalPages = computed(() => Math.ceil(filteredServicios.value.length / itemsPerPage));

    const paginatedServicios = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      return filteredServicios.value.slice(start, end);
    });

    const visiblePages = computed(() => {
      const pages = [];
      const total = totalPages.value;
      let start = Math.max(1, currentPage.value - 2);
      let end = Math.min(total, start + 4);
      
      if (end - start < 4) {
        start = Math.max(1, end - 4);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    });

    watch(searchQuery, () => {
      currentPage.value = 1;
    });

    const formatPrice = (price) => {
      return Number(price).toFixed(2);
    };

    const getWhatsAppConsultarUrl = (servicio) => {
      const number = '584125696219';
      const text = encodeURIComponent(`Hola SYSTEM JJ. Estoy interesado en consultar sobre el servicio: *${servicio.nombre}* (Cod: ${servicio.codigo || 'N/A'}).`);
      return `https://wa.me/${number}?text=${text}`;
    };

    const getWhatsAppContratarUrl = (servicio) => {
      const number = '584125696219';
      const text = encodeURIComponent(`Hola SYSTEM JJ. Quiero CONTRATAR el servicio: *${servicio.nombre}* (Cod: ${servicio.codigo || 'N/A'}) por el precio estimado de $${formatPrice(servicio.precioUsd)}.`);
      return `https://wa.me/${number}?text=${text}`;
    };

    const fetchTasaVenta = async () => {
      try {
        const result = await obtenerTasaVentaActual();
        if (result && result.success && result.data) {
          tasaVenta.value = result.data.precio_usd_bs || 0;
          fuenteTasa.value = result.data.nombreFuente || '';
          console.log('Tasa de venta actual cargada:', tasaVenta.value, 'Fuente:', fuenteTasa.value);
        }
      } catch (error) {
        console.error('Error cargando tasa de venta:', error);
      }
    };

    const formatBsPrice = (priceInUsd) => {
      if (!tasaVenta.value) return '';
      const totalBs = priceInUsd * tasaVenta.value;
      return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(totalBs) + ' Bs';
    };

    onMounted(() => {
      fetchServicios();
      fetchTasaVenta();
    });

    return {
      servicios,
      loadingServicios,
      searchQuery,
      currentPage,
      totalPages,
      filteredServicios,
      paginatedServicios,
      visiblePages,
      mobileMenuOpen,
      formatPrice,
      getWhatsAppConsultarUrl,
      getWhatsAppContratarUrl,
      tasaVenta,
      fuenteTasa,
      formatBsPrice
    };
  }
}
</script>
<style scoped>
@keyframes blob {
  0% { transform: translate(0px, 0px) scale(1); }
  33% { transform: translate(30px, -50px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
  100% { transform: translate(0px, 0px) scale(1); }
}
.animate-blob { animation: blob 7s infinite; }
.animation-delay-2000 { animation-delay: 2s; }
.animation-delay-4000 { animation-delay: 4s; }
</style>
